import {
  CircleDot,
  LayoutDashboard,
  DollarSign,
  History,
  Search,
  Settings,
  BarChart3,
  BrainCircuit,
  GitBranch,
  Wrench,
  ChevronsLeft,
  ChevronsRight,
  Activity,
  LogOut,
  Users,
  Shield,
  Building2,
  ClipboardList,
  MessageCircle,
  BookOpen,
  Layers,
  ScrollText,
  Sparkles,
  Bell,
  LineChart,
  Home,
  Inbox,
  Zap,
} from "lucide-react";
import { useViewMode } from "../hooks/useViewMode";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@/lib/router";
import { MedworkLogo } from "./MedworkLogo";
import { SidebarSection } from "./SidebarSection";
import { SidebarNavItem } from "./SidebarNavItem";
import { SidebarProjects } from "./SidebarProjects";
import { SidebarAgents } from "./SidebarAgents";
import { usePractice } from "../context/PracticeContext";
import { useSidebar } from "../context/SidebarContext";
import { sidebarBadgesApi } from "../api/sidebarBadges";
import { heartbeatsApi } from "../api/heartbeats";
import { authApi } from "../api/auth";
import { queryKeys } from "../lib/queryKeys";
import { Button } from "@/components/ui/button";
import { cn } from "../lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export function Sidebar() {
  const { selectedPracticeId, selectedPractice } = usePractice();
  const { toggleSidebar, collapsed } = useSidebar();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { data: session } = useQuery({
    queryKey: queryKeys.auth.session,
    queryFn: () => authApi.getSession(),
    retry: false,
  });
  const { data: sidebarBadges } = useQuery({
    queryKey: queryKeys.sidebarBadges(selectedPracticeId!),
    queryFn: () => sidebarBadgesApi.get(selectedPracticeId!),
    enabled: !!selectedPracticeId,
  });
  const { data: liveRuns } = useQuery({
    queryKey: queryKeys.liveRuns(selectedPracticeId!),
    queryFn: () => heartbeatsApi.liveRunsForCompany(selectedPracticeId!),
    enabled: !!selectedPracticeId,
    refetchInterval: 10_000,
  });
  const liveRunCount = liveRuns?.length ?? 0;
  const { mode, toggle: toggleMode } = useViewMode();
  const isSimple = mode === "simple";

  function openSearch() {
    document.dispatchEvent(new KeyboardEvent("keydown", { key: "k", metaKey: true }));
  }

  return (
    <aside className={cn("h-full min-h-0 border-r border-border bg-background flex flex-col", collapsed ? "w-14" : "w-60")}>
      {/* Branding header — practice logo + MedWork */}
      <div className={cn("shrink-0 border-b border-border", collapsed ? "flex items-center justify-center px-1 py-3" : "px-3 py-3")}>
        {collapsed ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                className="text-muted-foreground"
                onClick={openSearch}
              >
                <Search className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">Search</TooltipContent>
          </Tooltip>
        ) : (
          <div className="flex items-center justify-between">
            <MedworkLogo className="h-7 w-auto" />
            <Button
              variant="ghost"
              size="icon-sm"
              className="text-muted-foreground shrink-0"
              onClick={openSearch}
            >
              <Search className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
      </div>

      <nav className={cn("flex-1 min-h-0 overflow-y-auto scrollbar-auto-hide flex flex-col gap-1 py-2", collapsed ? "px-1 items-center" : "px-3")}>
        {/* Top-level nav — Simple mode shows 4 consumer entries; Pro mode shows the classic two. */}
        {isSimple ? (
          <div className="flex flex-col gap-0.5 w-full">
            <SidebarNavItem to="/home" label="Home" icon={Home} liveCount={liveRunCount} />
            <SidebarNavItem to="/ask" label="Ask" icon={MessageCircle} />
            <SidebarNavItem to="/analytics" label="Dashboards" icon={BarChart3} />
            <SidebarNavItem to="/inbox" label="Inbox" icon={Inbox} badge={sidebarBadges?.inbox} />
          </div>
        ) : (
          <div className="flex flex-col gap-0.5 w-full">
            <SidebarNavItem to="/dashboard" label="Dashboard" icon={LayoutDashboard} liveCount={liveRunCount} />
            <SidebarNavItem to="/analytics" label="Analytics" icon={BarChart3} />
          </div>
        )}

        {/* BUILD — Design & configure agents/workflows. Hidden in Simple mode (collapses under "Build" advanced group below). */}
        {!isSimple && (collapsed ? (
          <div className="flex flex-col gap-0.5 w-full">
            <SidebarNavItem to="/studio" label="Agent Studio" icon={BrainCircuit} />
            <SidebarNavItem to="/workflows" label="Workflow Studio" icon={GitBranch} />
            <SidebarNavItem to="/custom-tools" label="Custom Tools" icon={Wrench} />
            <SidebarNavItem to="/chat" label="Agent Chat" icon={MessageCircle} />
            <SidebarNavItem to="/explore" label="Analytics Studio" icon={LineChart} />
          </div>
        ) : (
          <SidebarSection label="Build">
            <SidebarNavItem to="/studio" label="Agent Studio" icon={BrainCircuit} />
            <SidebarNavItem to="/workflows" label="Workflow Studio" icon={GitBranch} />
            <SidebarNavItem to="/custom-tools" label="Custom Tools" icon={Wrench} />
            <SidebarNavItem to="/chat" label="Agent Chat" icon={MessageCircle} />
            <SidebarNavItem to="/explore" label="Analytics Studio" icon={LineChart} />
          </SidebarSection>
        ))}

        {!isSimple && (collapsed ? (
          <div className="flex flex-col gap-0.5 w-full">
            <SidebarNavItem to="/agents" label="Agents" icon={Activity} />
          </div>
        ) : (
          <SidebarAgents />
        ))}

        {/* OPERATIONS — Day-to-day work */}
        {!isSimple && (collapsed ? (
          <div className="flex flex-col gap-0.5 w-full">
            <SidebarNavItem to="/issues" label="Issues" icon={CircleDot} />
          </div>
        ) : (
          <SidebarSection label="Operations">
            <SidebarNavItem to="/issues" label="Issues" icon={CircleDot} badge={sidebarBadges?.inbox} />
          </SidebarSection>
        ))}

        {!isSimple && !collapsed && <SidebarProjects />}

        {/* OBSERVE — Monitoring, tracing, costs */}
        {!isSimple && (collapsed ? (
          <div className="flex flex-col gap-0.5 w-full">
            <SidebarNavItem to="/activity" label="Activity" icon={History} />
            <SidebarNavItem to="/costs" label="Costs" icon={DollarSign} />
            <SidebarNavItem to="/tech-workflow" label="System Blueprint" icon={Layers} />
            <SidebarNavItem to="/docs" label="Documentation" icon={BookOpen} />
          </div>
        ) : (
          <SidebarSection label="Observe">
            <SidebarNavItem to="/activity" label="Activity" icon={History} />
            <SidebarNavItem to="/costs" label="Costs" icon={DollarSign} />
            <SidebarNavItem to="/tech-workflow" label="System Blueprint" icon={Layers} />
            <SidebarNavItem to="/docs" label="Documentation" icon={BookOpen} />
          </SidebarSection>
        ))}

        {/* ADMINISTRATION — Settings, users, integrations */}
        {!isSimple && (collapsed ? (
          <div className="flex flex-col gap-0.5 w-full">
            <SidebarNavItem to="/admin/practices" label="Practices" icon={Building2} />
            <SidebarNavItem to="/admin/users" label="Users" icon={Users} />
            <SidebarNavItem to="/admin/roles" label="Roles" icon={Shield} />
            <SidebarNavItem to="/admin/metric-proposals" label="Metric Proposals" icon={Sparkles} />
            <SidebarNavItem to="/admin/contexts" label="Agent Contexts" icon={ScrollText} />
            <SidebarNavItem to="/admin/budget" label="Budget" icon={DollarSign} />
            <SidebarNavItem to="/admin/alert-channels" label="Alert Channels" icon={Bell} />
            <SidebarNavItem to="/admin/activity-log" label="Activity Log" icon={ClipboardList} />
            <SidebarNavItem to="/company/settings" label="Practice Settings" icon={Settings} />
          </div>
        ) : (
          <SidebarSection label="Administration">
            <SidebarNavItem to="/admin/practices" label="Practices" icon={Building2} />
            <SidebarNavItem to="/admin/users" label="Users" icon={Users} />
            <SidebarNavItem to="/admin/roles" label="Roles" icon={Shield} />
            <SidebarNavItem to="/admin/metric-proposals" label="Metric Proposals" icon={Sparkles} />
            <SidebarNavItem to="/admin/contexts" label="Agent Contexts" icon={ScrollText} />
            <SidebarNavItem to="/admin/budget" label="Budget" icon={DollarSign} />
            <SidebarNavItem to="/admin/alert-channels" label="Alert Channels" icon={Bell} />
            <SidebarNavItem to="/admin/activity-log" label="Activity Log" icon={ClipboardList} />
            <SidebarNavItem to="/company/settings" label="Practice Settings" icon={Settings} />
          </SidebarSection>
        ))}

        {/* SIMPLE-MODE: 3 collapsed Advanced groups containing the Pro surface. */}
        {isSimple && !collapsed && (
          <div className="flex flex-col gap-1 w-full pt-2 mt-1 border-t border-border">
            <details className="group">
              <summary className="flex items-center gap-2 px-2 py-1.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground list-none [&::-webkit-details-marker]:hidden">
                <ChevronsRight className="h-3 w-3 transition-transform group-open:rotate-90" />
                Agents
              </summary>
              <div className="flex flex-col gap-0.5 mt-1">
                <SidebarNavItem to="/studio" label="Agent Studio" icon={BrainCircuit} />
                <SidebarNavItem to="/agents" label="All agents" icon={Activity} />
                <SidebarNavItem to="/agents/patient-journey-monitor" label="Patient Journey Monitor" icon={CircleDot} />
              </div>
            </details>
            <details className="group">
              <summary className="flex items-center gap-2 px-2 py-1.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground list-none [&::-webkit-details-marker]:hidden">
                <ChevronsRight className="h-3 w-3 transition-transform group-open:rotate-90" />
                Build
              </summary>
              <div className="flex flex-col gap-0.5 mt-1">
                <SidebarNavItem to="/workflows" label="Workflow Studio" icon={GitBranch} />
                <SidebarNavItem to="/custom-tools" label="Custom Tools" icon={Wrench} />
                <SidebarNavItem to="/chat" label="Agent Chat" icon={MessageCircle} />
                <SidebarNavItem to="/explore" label="Analytics Studio" icon={LineChart} />
              </div>
            </details>
            <details className="group">
              <summary className="flex items-center gap-2 px-2 py-1.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground list-none [&::-webkit-details-marker]:hidden">
                <ChevronsRight className="h-3 w-3 transition-transform group-open:rotate-90" />
                Admin
              </summary>
              <div className="flex flex-col gap-0.5 mt-1">
                <SidebarNavItem to="/admin/practices" label="Practices" icon={Building2} />
                <SidebarNavItem to="/admin/users" label="Users" icon={Users} />
                <SidebarNavItem to="/admin/roles" label="Roles" icon={Shield} />
                <SidebarNavItem to="/admin/metric-proposals" label="Metric Proposals" icon={Sparkles} />
                <SidebarNavItem to="/admin/contexts" label="Agent Contexts" icon={ScrollText} />
            <SidebarNavItem to="/admin/budget" label="Budget" icon={DollarSign} />
                <SidebarNavItem to="/admin/alert-channels" label="Alert Channels" icon={Bell} />
                <SidebarNavItem to="/admin/activity-log" label="Activity Log" icon={ClipboardList} />
                <SidebarNavItem to="/company/settings" label="Practice Settings" icon={Settings} />
                <SidebarNavItem to="/activity" label="Activity" icon={History} />
                <SidebarNavItem to="/costs" label="Costs" icon={DollarSign} />
                <SidebarNavItem to="/tech-workflow" label="System Blueprint" icon={Layers} />
                <SidebarNavItem to="/docs" label="Documentation" icon={BookOpen} />
              </div>
            </details>
            <button
              className="flex items-center gap-2 px-2 py-1.5 mt-2 rounded-md text-[11px] text-muted-foreground hover:bg-accent/30 hover:text-foreground"
              onClick={toggleMode}
              title="Switch to Pro mode"
            >
              <Zap className="h-3 w-3" />
              Switch to Pro mode
            </button>
          </div>
        )}

        {/* PRO MODE: footer link back to Simple */}
        {!isSimple && !collapsed && (
          <button
            className="flex items-center gap-2 px-2 py-1.5 mt-2 rounded-md text-[11px] text-muted-foreground hover:bg-accent/30 hover:text-foreground"
            onClick={toggleMode}
            title="Switch back to Simple mode"
          >
            <Home className="h-3 w-3" />
            Switch to Simple mode
          </button>
        )}

        {/* Live agent indicator */}
        {liveRunCount > 0 && !collapsed && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800/40">
            <Activity className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400 animate-pulse" />
            <span className="text-[11px] font-medium text-emerald-700 dark:text-emerald-300">
              {liveRunCount} agent{liveRunCount !== 1 ? "s" : ""} running
            </span>
          </div>
        )}
        {liveRunCount > 0 && collapsed && (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center justify-center w-full py-2">
                <Activity className="h-4 w-4 text-emerald-600 dark:text-emerald-400 animate-pulse" />
              </div>
            </TooltipTrigger>
            <TooltipContent side="right">{liveRunCount} agent{liveRunCount !== 1 ? "s" : ""} running</TooltipContent>
          </Tooltip>
        )}
      </nav>

      {/* Footer: Collapse + User profile */}
      <div className="shrink-0 border-t border-border">
        <div className={cn("flex items-center", collapsed ? "justify-center px-1 py-2" : "justify-end px-3 py-2")}>
          {collapsed ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  className="text-muted-foreground"
                  onClick={toggleSidebar}
                  aria-label="Expand sidebar"
                  title="Expand sidebar"
                >
                  <ChevronsRight className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">Expand</TooltipContent>
            </Tooltip>
          ) : (
            <Button
              variant="ghost"
              size="icon-sm"
              className="text-muted-foreground shrink-0"
              onClick={toggleSidebar}
              aria-label="Collapse sidebar"
              title="Collapse sidebar"
            >
              <ChevronsLeft className="h-4 w-4" />
            </Button>
          )}
        </div>

        {/* User profile + Logout */}
        {session?.user && (
          <div className={cn("border-t border-border flex items-center", collapsed ? "justify-center px-1 py-2.5" : "px-3 py-2.5 gap-3")}>
            {session.user.image ? (
              <img
                src={session.user.image}
                alt={session.user.name ?? "User"}
                className="h-8 w-8 rounded-full object-cover shrink-0"
              />
            ) : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="h-8 w-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-semibold shrink-0 cursor-default">
                    {(session.user.name ?? session.user.email ?? "U")
                      .split(" ")
                      .map((w) => w[0])
                      .join("")
                      .slice(0, 2)
                      .toUpperCase()}
                  </div>
                </TooltipTrigger>
                {collapsed && <TooltipContent side="right">{session.user.name ?? session.user.email ?? "User"}</TooltipContent>}
              </Tooltip>
            )}
            {!collapsed && (
              <>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-medium text-foreground truncate">
                    {session.user.name ?? session.user.email ?? "User"}
                  </p>
                  <p className="text-[10px] text-blue-600 leading-tight">Account</p>
                </div>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  className="text-muted-foreground shrink-0 hover:text-destructive"
                  title="Sign out"
                  onClick={async () => {
                    await authApi.signOut();
                    queryClient.clear();
                    navigate("/auth", { replace: true });
                  }}
                >
                  <LogOut className="h-4 w-4" />
                </Button>
              </>
            )}
          </div>
        )}
      </div>
    </aside>
  );
}
